import { useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  House, ClipboardList, Activity, UsersRound, Settings, LogOut,
  Receipt, Archive, Wallet, TrendingUp,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { WS_TOKENS } from "@/lib/workspaceTheme";

/**
 * Fixed right-anchored sidebar for desktop (RTL) workspace.
 * Width: 248px. Height: 100vh. Icons only on hover collapse (future).
 *
 * IMPORTANT: All destinations are existing routes (verified in App.js).
 * We do NOT create new pages here — pure navigation.
 */
export default function WorkspaceSidebar() {
  const { user, logout } = useAuth();
  const { theme, lang, dir } = useI18n();
  const navigate = useNavigate();

  const isRoot = !!user?.role?.is_root;
  const roleCode = user?.role?.code;
  const isManagement = ["general_manager", "deputy_manager"].includes(roleCode);
  const canSeeFinance = isRoot || isManagement || roleCode === "accountant";
  const canSeeSettings = isRoot || isManagement;
  const canSeeAdminNav = isRoot || isManagement;         // Follow-up, Clients, Accountant, Performance
  const isEmployee = roleCode === "employee";

  const items = useMemo(() => {
    const arr = [
      { key: "home",         to: "/",             icon: House,          label: lang === "ar" ? "الرئيسية"   : "Home" },
      { key: "applications", to: "/applications", icon: ClipboardList,  label: lang === "ar" ? "المعاملات" : "Applications" },
    ];

    // Follow-up — admins/managers only (tight: ≤10 days or SLA breached)
    if (canSeeAdminNav) {
      arr.push({
        key: "follow",
        to: "/applications?view=follow_up",
        icon: Activity,
        label: lang === "ar" ? "المتابعة" : "Follow-up",
      });
    }

    // Invoices — visible to everyone in the workspace
    arr.push({
      key: "invoices",
      to: "/applications?view=invoices",
      icon: Receipt,
      label: lang === "ar" ? "الفواتير" : "Invoices",
    });

    if (canSeeAdminNav) {
      arr.push({ key: "clients",     to: "/admin/users",         icon: UsersRound,  label: lang === "ar" ? "العملاء"    : "Clients" });
      arr.push({ key: "performance", to: "/admin/performance",   icon: TrendingUp,  label: lang === "ar" ? "التقييم"    : "Performance" });
    }
    if (canSeeFinance) {
      arr.push({ key: "accountant", to: "/finance", icon: Wallet, label: lang === "ar" ? "المحاسبة" : "Accounting" });
    }

    // Archive — visible to everyone (employees + admins) so they can restore
    arr.push({
      key: "archive",
      to: "/applications?archived=1",
      icon: Archive,
      label: lang === "ar" ? "الأرشيف" : "Archive",
    });

    return arr;
  }, [lang, canSeeAdminNav, canSeeFinance]);

  const css = {
    aside: {
      position: "fixed",
      top: 0,
      bottom: 0,
      [dir === "rtl" ? "right" : "left"]: 0,
      width: WS_TOKENS.sidebarWidth,
      background: theme.sidebarBg,
      borderInlineStart: `1px solid ${theme.borderSoft}`,
      display: "flex",
      flexDirection: "column",
      padding: `${WS_TOKENS.space[5]}px ${WS_TOKENS.space[3]}px`,
      zIndex: 100,
      fontFamily: "'Tajawal','Segoe UI', sans-serif",
    },
    brand: {
      display: "flex", alignItems: "center", gap: 10,
      padding: `0 ${WS_TOKENS.space[2]}px`,
      marginBottom: WS_TOKENS.space[5],
      cursor: "pointer",
    },
    brandMark: {
      width: 36, height: 36, borderRadius: 10,
      background: theme.goldGradient,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      color: "#0A0F19", fontWeight: 900, fontSize: 15, letterSpacing: 0.3,
    },
    brandText: { color: theme.text, fontWeight: 800, fontSize: 15, letterSpacing: 0.3 },
    nav: { display: "flex", flexDirection: "column", gap: 4, flex: 1 },
    link: ({ isActive }) => ({
      display: "flex", alignItems: "center", gap: 12,
      padding: `10px 12px`,
      borderRadius: WS_TOKENS.radius.md,
      color: isActive ? theme.text : theme.textSoft,
      background: isActive ? "rgba(255,255,255,0.06)" : "transparent",
      textDecoration: "none",
      fontSize: 14, fontWeight: isActive ? 700 : 500,
      transition: `background ${WS_TOKENS.motion.fast}`,
      minHeight: WS_TOKENS.touch,
      position: "relative",
    }),
    activeBar: {
      position: "absolute",
      insetInlineStart: 0, top: 10, bottom: 10, width: 3,
      background: theme.gold,
      borderRadius: 2,
    },
    footer: {
      display: "flex", flexDirection: "column", gap: 4,
      paddingTop: WS_TOKENS.space[3],
      borderTop: `1px solid ${theme.borderSoft}`,
    },
    ftBtn: {
      display: "flex", alignItems: "center", gap: 12,
      padding: `10px 12px`,
      borderRadius: WS_TOKENS.radius.md,
      background: "transparent", border: "none",
      color: theme.textSoft, fontFamily: "inherit",
      fontSize: 14, cursor: "pointer",
      minHeight: WS_TOKENS.touch,
    },
    user: {
      display: "flex", alignItems: "center", gap: 10,
      padding: `10px 12px 12px`, marginBottom: 4,
    },
    userAvatar: {
      width: 34, height: 34, borderRadius: "50%",
      background: `linear-gradient(135deg, ${theme.gold}, ${theme.goldDeep})`,
      color: "#0A0F19", fontWeight: 900, fontSize: 13,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    },
    userMeta: { display: "flex", flexDirection: "column", minWidth: 0 },
    userName: { fontSize: 13, fontWeight: 700, color: theme.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 },
    userRole: { fontSize: 11, color: theme.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 },
  };

  const initial = (user?.full_name || user?.username || "?").trim().charAt(0).toUpperCase();
  const roleName = user?.role ? (lang === "en" ? user.role.name_en || user.role.name_ar : user.role.name_ar) : "";

  return (
    <aside style={css.aside} data-testid="workspace-sidebar" dir={dir}>
      <div style={css.brand} onClick={() => navigate("/")} data-testid="sidebar-brand">
        <div style={css.brandMark}>V</div>
        <div style={css.brandText}>VTA</div>
      </div>

      <nav style={css.nav} aria-label="workspace-nav">
        {items.map(({ key, to, icon: Icon, label }) => {
          // Custom active detection because NavLink only matches pathname,
          // but several of our items share `/applications` with different
          // query strings (Invoices vs Follow-up vs Archive vs Applications).
          const [toPath, toQuery] = to.split("?");
          const currentPath = window.location.pathname;
          const currentSearch = window.location.search.replace(/^\?/, "");
          let isActive = false;
          if (toPath === "/") {
            isActive = currentPath === "/";
          } else if (currentPath.startsWith(toPath)) {
            if (!toQuery) {
              // Applications item (bare): active only when no view/archived qs.
              const q = new URLSearchParams(currentSearch);
              isActive = key === "applications"
                ? (!q.get("view") && q.get("archived") !== "1")
                : true;
            } else {
              // Item with query: match those params exactly.
              const want = new URLSearchParams(toQuery);
              const have = new URLSearchParams(currentSearch);
              isActive = Array.from(want.entries())
                .every(([k, v]) => have.get(k) === v);
            }
          }
          return (
            <NavLink
              key={key}
              to={to}
              style={css.link}
              data-testid={`sidebar-link-${key}`}
            >
              {() => (
                <>
                  {isActive && <span style={css.activeBar} aria-hidden />}
                  <Icon size={20} strokeWidth={1.75} color={isActive ? theme.gold : theme.textSoft} />
                  <span style={{ color: isActive ? theme.text : theme.textSoft, fontWeight: isActive ? 800 : 600 }}>{label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div style={css.footer}>
        <div style={css.user} data-testid="sidebar-user">
          <div style={css.userAvatar}>{initial}</div>
          <div style={css.userMeta}>
            <div style={css.userName}>{user?.full_name || user?.username}</div>
            <div style={css.userRole}>{roleName}</div>
          </div>
        </div>
        {canSeeSettings && (
          <button
            type="button"
            onClick={() => navigate("/admin/settings")}
            style={css.ftBtn}
            data-testid="sidebar-settings"
          >
            <Settings size={20} strokeWidth={1.75} />
            <span>{lang === "ar" ? "الإعدادات" : "Settings"}</span>
          </button>
        )}
        <button
          type="button"
          onClick={async () => { await logout(); navigate("/login"); }}
          style={css.ftBtn}
          data-testid="sidebar-logout"
        >
          <LogOut size={20} strokeWidth={1.75} />
          <span>{lang === "ar" ? "خروج" : "Logout"}</span>
        </button>
        {/* NOTE: intentionally NO role badge, NO permissions badge in the
            sidebar. Per redesign brief the role belongs to the account panel,
            not the primary chrome. */}
        <div style={{ height: 4 }} />
        <div style={{ fontSize: 10, color: theme.textFaint, textAlign: "center", padding: "4px 6px" }}>
          {roleCode === "employee" ? (lang === "ar" ? "موظف" : "Employee") : ""}
        </div>
      </div>
    </aside>
  );
}
